import { Component, ErrorInfo, ReactNode } from 'react'
import { EnhancedError, handleError } from '../../utils/errorHandling'
import type { AppError, UserId } from '../../types'

/**
 * Props for the ErrorBoundary component
 * @interface Props
 */
interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: AppError, errorInfo: ErrorInfo) => void
  isolateError?: boolean
  retryable?: boolean
  maxRetries?: number
  context?: string
  userId?: UserId
  /** Specific error type for specialized handling */
  errorType?: 'general' | 'data-loading' | 'social-feed' | 'dating' | 'events' | 'admin' | 'wallet'
  /** Custom error message to display */
  customMessage?: string
  /** Whether to show developer details in production */
  showDetails?: boolean
}

/**
 * State for the ErrorBoundary component
 * @interface State
 */
interface State {
  hasError: boolean
  error?: Error
  errorInfo?: ErrorInfo
  retryCount: number
  isRecovering: boolean
  errorId: string
}

export class ErrorBoundary extends Component<Props, State> {
  private retryTimeoutId?: NodeJS.Timeout

  constructor(props: Props) {
    super(props)
    this.state = { 
      hasError: false, 
      retryCount: 0, 
      isRecovering: false,
      errorId: ''
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    return { 
      hasError: true, 
      error,
      errorId
    }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Create enhanced error for better tracking
    const enhancedError = error instanceof EnhancedError 
      ? error 
      : new EnhancedError(
          'COMPONENT_ERROR',
          error.message,
          {
            severity: 'high',
            context: {
              component: this.props.context || 'Unknown'
            },
            userId: this.props.userId,
            retryable: this.props.retryable ?? true
          }
        )

    this.setState({ error, errorInfo })
    
    // Handle error with enhanced error handling
    handleError(enhancedError, {
      component: this.props.context || 'ErrorBoundary'
    })

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(enhancedError.toAppError(), errorInfo)
    }

    // Auto-retry if configured
    if (this.props.retryable && this.state.retryCount < (this.props.maxRetries || 3)) {
      this.scheduleRetry()
    }
  }

  private scheduleRetry = () => {
    this.setState({ isRecovering: true })
    
    const delay = Math.min(1000 * Math.pow(2, this.state.retryCount), 30000) // Max 30s delay
    
    this.retryTimeoutId = setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        retryCount: prevState.retryCount + 1,
        isRecovering: false,
        errorId: prevState.errorId
      } as State))
    }, delay)
  }

  private handleManualRetry = () => {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
    }
    
    this.setState(prevState => ({
      hasError: false,
      retryCount: prevState.retryCount + 1,
      isRecovering: false,
      errorId: prevState.errorId
    } as State))
  }

  private handleReload = () => {
    // Clear any pending retries
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
    }
    window.location.reload()
  }

  override componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId)
    }
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Show recovery UI if retrying
      if (this.state.isRecovering) {
        return (
          <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
            <div className="max-w-md w-full text-center">
              <div className="w-16 h-16 mx-auto mb-4 border-4 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
              <h2 className="text-xl font-semibold text-white mb-2">Recovering...</h2>
              <p className="text-dark-300">Attempting to restore the application</p>
            </div>
          </div>
        )
      }

      const canRetry = this.props.retryable && this.state.retryCount < (this.props.maxRetries || 3)
      const isComponentError = this.props.context && this.props.isolateError

      return (
        <div className={`${isComponentError ? 'bg-dark-900 border border-dark-700 rounded-lg p-6 m-4' : 'min-h-screen bg-dark-950'} flex items-center justify-center p-4`}>
          <div className="max-w-md w-full text-center">
            <div className="mb-8">
              <div className="w-24 h-24 mx-auto mb-4 bg-red-600 rounded-full flex items-center justify-center">
                <svg 
                  className="w-12 h-12 text-white" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" 
                  />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {isComponentError ? `${this.props.context} Error` : 'Something went wrong'}
              </h1>
              <p className="text-dark-300 mb-2">
                {isComponentError 
                  ? `There was an issue with the ${this.props.context} component.`
                  : 'We\'re sorry, but something unexpected happened.'
                }
              </p>
              {this.state.retryCount > 0 && (
                <p className="text-yellow-400 text-sm mb-4">
                  Retry attempt: {this.state.retryCount}/{this.props.maxRetries || 3}
                </p>
              )}
              <p className="text-dark-400 text-xs mb-4">
                Error ID: {this.state.errorId}
              </p>
            </div>

            <div className="space-y-4">
              {canRetry && (
                <button
                  onClick={this.handleManualRetry}
                  className="btn-primary w-full"
                >
                  Try Again
                </button>
              )}
              
              {!isComponentError && (
                <button
                  onClick={this.handleReload}
                  className="btn-secondary w-full"
                >
                  Reload Page
                </button>
              )}

              {isComponentError && (
                <button
                  onClick={() => this.setState({ 
                    hasError: false,
                    retryCount: 0,
                    isRecovering: false,
                    errorId: ''
                  } as State)}
                  className="btn-secondary w-full"
                >
                  Continue Without This Feature
                </button>
              )}
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="cursor-pointer text-dark-400 hover:text-dark-300 mb-2">
                  Error Details (Development)
                </summary>
                <div className="bg-dark-900 border border-dark-700 rounded-lg p-4 text-sm font-mono text-red-400 overflow-auto max-h-64">
                  <div className="mb-4">
                    <strong>Error:</strong> {this.state.error.message}
                  </div>
                  <div className="mb-4">
                    <strong>Context:</strong> {this.props.context || 'Global'}
                  </div>
                  <div className="mb-4">
                    <strong>Stack:</strong>
                    <pre className="whitespace-pre-wrap text-xs mt-1">
                      {this.state.error.stack}
                    </pre>
                  </div>
                  {this.state.errorInfo && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="whitespace-pre-wrap text-xs mt-1">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      )
    }

    return this.props.children
  }
}